import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  split,
} from "@apollo/client";

import { getMainDefinition } from "@apollo/client/utilities";
import { LOCAL_STORAGE_KEYS } from "../common/constants";
import { ApolloLink } from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { authLink, errorLink } from "./authLink";
import { getAppTokenHeaders } from "./appTokenHeaders";
import { RefreshTokenDocument } from "./generated/hooks";

const normalizeEndpoint = (value: string) =>
  value.replace("api.robinfood.dev", "api.stg.robinfood.dev");

const fallbackHttpEndpoint =
  process.env.REACT_APP_ENV === "prod"
    ? "https://api.robinfood.app"
    : "https://api.stg.robinfood.dev";

const fallbackWsEndpoint =
  process.env.REACT_APP_ENV === "prod"
    ? "wss://api.robinfood.app"
    : "wss://api.stg.robinfood.dev";

export const endpoint = normalizeEndpoint(
  process.env.REACT_APP_GRAPHQL_ENDPOINT || fallbackHttpEndpoint
);

const wsEndpoint = normalizeEndpoint(
  process.env.REACT_APP_GRAPHQL_WS_ENDPOINT || fallbackWsEndpoint
);

// Store WebSocket client reference for reconnection
let wsClient: ReturnType<typeof createClient> | null = null;
let isRedirectingToLogin = false;

function redirectToLogin() {
  const currentPath = window.location.pathname;
  const isPublicRoute =
    currentPath.startsWith("/invite/") ||
    currentPath.startsWith("/login") ||
    currentPath.startsWith("/forgot-password") ||
    currentPath.startsWith("/reset-password/");

  localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);

  if (isPublicRoute || isRedirectingToLogin) {
    return;
  }

  isRedirectingToLogin = true;
  window.location.replace("/login");
}

// Function to refresh token for WebSocket subscriptions
async function refreshTokenForWs(): Promise<string | null> {
  try {
        const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    
    if (!refreshToken) {
      return null;
    }
    
    // Create a temporary HTTP client for refresh
    const httpLink = createHttpLink({
      uri: `${endpoint}/graphql`,
      headers: getAppTokenHeaders(),
    });
    
    const tempClient = new ApolloClient({
      link: httpLink,
      cache: new InMemoryCache(),
    });

    const result = await tempClient.mutate({
      mutation: RefreshTokenDocument,
      variables: {
        refreshToken,
      },
    });

    if (!result.data?.refreshToken) {
      return null;
    }

    const { accessToken, refreshToken: newRefreshToken } = result.data.refreshToken;

    // Update tokens in localStorage
    localStorage.setItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN, accessToken);
    if (newRefreshToken) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
    }

    return accessToken;
  } catch (error) {
    return null;
  }
}

function createWSLink() {
  const wsUrl = `${wsEndpoint}/graphql`;
  
  wsClient = createClient({
    url: wsUrl,
    lazy: true, // Only connect when needed, allowing fresh token on each connection
    connectionParams: async () => {
      const token = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
      return {
        headers: {
          ...getAppTokenHeaders(),
          authorization: token ? `Bearer ${token}` : "",
        },
      };
    },
    retryAttempts: 5,
    shouldRetry: (errOrCloseEvent) => {
      // Retry on network errors but not on auth errors (we'll handle those separately)
      if (errOrCloseEvent && typeof errOrCloseEvent === 'object') {
        const event = errOrCloseEvent as CloseEvent;
        // WebSocket close codes: 4401 = Unauthorized, 4403 = Forbidden
        if (event.code === 4401 || event.code === 4403 || event.code === 1008) {
          return false; // Don't auto-retry, we'll handle manually
        }
      }
      return true;
    },
    on: {
      error: async (error: any) => {
        // Check if it's an auth error
        const errorMessage = (error as Error)?.message || String(error);
        if (
          errorMessage.includes('Unauthorized') ||
          errorMessage.includes('Forbidden') ||
          errorMessage.includes('jwt expired') ||
          errorMessage.includes('invalid token') ||
          errorMessage.includes('UNAUTHENTICATED') ||
          errorMessage.includes('FORBIDDEN')
        ) {
          // Try to refresh the token
          const newToken = await refreshTokenForWs();
          
          if (!newToken) {
            redirectToLogin();
          }
        }
      },
      closed: async (event: CloseEvent | any) => {
        // Handle auth-related close codes
        if (event?.code === 4401 || event?.code === 4403 || event?.code === 1008) {
          const newToken = await refreshTokenForWs();
          
          if (!newToken) {
            redirectToLogin();
          }
        }
      },
    },
  });
  
  return new GraphQLWsLink(wsClient);
}

function createGraphQLHttpLink() {
  return createHttpLink({
    uri: `${endpoint}/graphql`,
    headers: getAppTokenHeaders(),
  });
}

export function createApolloClient() {
  // Create links
  const httpLink = createGraphQLHttpLink();
  const wsLink = createWSLink();

  // Split between WebSocket (for subscriptions) and HTTP (for queries/mutations)
  const splitLink = split(
    // Split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    httpLink
  );

  return new ApolloClient({
    link: ApolloLink.from([errorLink, authLink, splitLink]),
    credentials: "include",
    cache: new InMemoryCache(),
  });
}
